<script setup>
import { useStore } from 'vuex'
import ActionBar from '@/components/common/ActionBar.vue'
import DataField from '@/components/form/DataField.vue'
import ComboBoxDataField from '@/components/form/ComboBoxDataField.vue'
import { debounce } from 'lodash'
import { computed, onMounted } from 'vue'
import { dirtyFieldMixin } from '@/mixins/dirtyFieldMixin'
import { ADMIN_ROLE, CONTRIBUTOR_ROLE } from '@/components/constant'

const { addDirty, getDirtyClass, dirtyItems } = dirtyFieldMixin()

const props = defineProps({
  number: {
    type: Number,
    required: true
  }
})

const store = useStore()
const currentItem = computed(() => store.state.multipleHeadLine.currentItem)
const currentSchematisationStage = computed(() => store.state.stabSchematisationStage.currentItem)
const isLocked = computed(() => store.getters['stabSchematisation/isLockedCurrent'])
const schematisationStagesToImport = computed(
  () => store.getters['stabSchematisationStage/previousSchematisationStagesHaveHeadLine']
)

const currentWaterLevel = computed(() => {
  const currentStageConfiguration = currentSchematisationStage.value?.stage_configuration
  return currentStageConfiguration ? currentStageConfiguration[`head_line_${props.number}`] : null
})
const riverWaterLevelOptions = computed(() => {
  const options = currentItem.value?.river_water_level_options ?? []
  return options.filter((option) => option.water_level === currentWaterLevel.value)
})
const polderWaterLevelOptions = computed(() => {
  const options = currentItem.value?.polder_water_level_options ?? []
  if (currentWaterLevel.value === 'extreme') {
    return options.filter((option) => option.water_level === 'high')
  }
  return options.filter((option) => option.water_level === currentWaterLevel.value)
})

const update = () => store.dispatch('multipleHeadLine/update')
const updateXLocation = () => store.dispatch('multipleHeadLine/updateXLocation')
const setNestedParameter = (payload) =>
  store.dispatch('multipleHeadLine/setNestedParameter', payload)
const emptyDirtyItems = () => store.dispatch('multipleHeadLine/emptyDirtyItems')
const toggleImportDialog = (value) => store.dispatch('multipleHeadLine/toggleImportDialog', value)

const currentRole = computed(() => store.getters['project/getCurrentRole'])

const riverWaterLevelAutoSelect = () => {
  if (
    riverWaterLevelOptions.value.length > 0 &&
    currentItem.value.number === props.number &&
    currentItem.value.river_water_level === null &&
    [ADMIN_ROLE, CONTRIBUTOR_ROLE].includes(currentRole.value)
  ) {
    setNestedParameter({
      item: currentItem.value,
      parameter: 'river_water_level',
      value: riverWaterLevelOptions.value[0]?.value
    })
    addDirty(currentItem.value, 'river_water_level')
  }
}
const polderWaterLevelAutoSelect = () => {
  if (
    polderWaterLevelOptions.value.length > 0 &&
    currentItem.value.number === props.number &&
    currentItem.value.polder_water_level === null &&
    [ADMIN_ROLE, CONTRIBUTOR_ROLE].includes(currentRole.value)
  ) {
    setNestedParameter({
      item: currentItem.value,
      parameter: 'polder_water_level',
      value: polderWaterLevelOptions.value[0]?.value
    })
    addDirty(currentItem.value, 'polder_water_level')
  }
}
const saveChangesWithDebounce = debounce(async function () {
  await updateXLocation()
  emptyDirtyItems()
  dirtyItems.value = []
}, 200)
const saveChanges = async () => {
  if (dirtyItems.value.length > 0) {
    await update()
    emptyDirtyItems()
    dirtyItems.value = []
  }
}
onMounted(() => {
  riverWaterLevelAutoSelect()
  polderWaterLevelAutoSelect()
})

defineExpose({ dirtyItems })
</script>

<template>
  <v-row no-gutters>
    <v-col cols="6" class="px-3">
      <ActionBar
        :disabled="isLocked"
        show-save
        @save="saveChanges()"
        show-import
        :disabled-import="schematisationStagesToImport.length === 0"
        @import="toggleImportDialog(true)"
      />
      <ComboBoxDataField
        label="River water level [m+NAP]"
        @set="setNestedParameter"
        @update="addDirty(currentItem, 'river_water_level')"
        :classField="[
          getDirtyClass(currentItem, 'river_water_level'),
          isLocked ? 'disable-events' : ''
        ]"
        parameter="river_water_level"
        :item="currentItem"
        :options="riverWaterLevelOptions"
      ></ComboBoxDataField>
      <ComboBoxDataField
        label="Polder water level [m+NAP]"
        class="mt-1"
        @set="setNestedParameter"
        @update="addDirty(currentItem, 'polder_water_level')"
        :classField="[
          getDirtyClass(currentItem, 'polder_water_level'),
          isLocked ? 'disable-events' : ''
        ]"
        parameter="polder_water_level"
        :item="currentItem"
        :options="polderWaterLevelOptions"
      ></ComboBoxDataField>

      <DataField
        label="Intrusion length [m]"
        @update="addDirty(currentItem, 'intrusion_length')"
        @set="setNestedParameter"
        :classField="[
          getDirtyClass(currentItem, 'intrusion_length'),
          isLocked ? 'disable-events' : ''
        ]"
        class="mt-1"
        density="default"
        parameter="intrusion_length"
        :item="currentItem"
      >
      </DataField>
    </v-col>

    <v-divider vertical></v-divider>

    <v-col cols="6" class="px-3">
      <DataField
        @set="setNestedParameter"
        label="X location for burst calculation"
        @mouseover="$emit('setActiveElement')"
        @mouseleave="$emit('deleteActiveElement')"
        @keyup="saveChangesWithDebounce"
        :classField="[isLocked ? 'disable-events' : '']"
        parameter="x_location"
        :item="currentItem"
      >
      </DataField>
      <DataField
        label="Downward pressure [kN/m3]"
        @set="setNestedParameter"
        :classField="[isLocked ? 'disable-events' : '']"
        parameter="downward_pressure"
        :item="currentItem"
        class="mt-5"
        :editable="false"
      >
      </DataField>

      <DataField
        label="Upward pressure [kN/m3]"
        @set="setNestedParameter"
        :classField="[isLocked ? 'disable-events' : '']"
        parameter="upward_pressure"
        :item="currentItem"
        class="mt-5"
        :editable="false"
      >
      </DataField>

      <DataField
        label="Ratio"
        @set="setNestedParameter"
        :classField="[isLocked ? 'disable-events' : '']"
        parameter="ratio"
        :item="currentItem"
        class="mt-5"
        :editable="false"
      >
      </DataField>
    </v-col>
  </v-row>
</template>
